import {
  Target,
  Building2,
  Clock,
  MessageCircle,
  Repeat,
  Eye,
  Rocket,
  BarChart3,
  Users,
  TrendingUp,
} from "lucide-react";

export const COACH_FEATURES = [
  {
    title: "Never Lose Track",
    description: "Know when your agents are on track, at-risk, or thriving.",
    icon: Target,
  },
  {
    title: "3X Your Capacity",
    description:
      "Handle 40+ agents with the same effort as 15. Scale your coaching business without burning out.",
    icon: TrendingUp,
  },
  {
    title: "Reclaim 8 Hours/Week",
    description:
      "Stop guessing who to follow up with, tracking down trainers, and chasing follow-ups. HelloPC handles the grind.",
    icon: Clock,
  },
  {
    title: "Conversation Memory",
    description:
      "Every touchpoint logged, categorized, and searchable. Remember what matters without the mental load.",
    icon: MessageCircle,
  },
];

export const BROKER_FEATURES = [
  {
    title: "Systemize Onboarding",
    description:
      "Every new agent gets the same process. Your system travels with the role, not the person.",
    icon: Repeat,
  },
  {
    title: "Real-Time Visibility",
    description:
      "Dashboard shows which agents are on track, at-risk, or thriving. No more guessing games.",
    icon: Eye,
  },
  {
    title: "Scale Without Headcount",
    description:
      "Your PCs can handle 2-3X more agents with HelloPC. Grow from 80 to 200 agents without hiring.",
    icon: Rocket,
  },
  {
    title: "Measure What Matters",
    description:
      "Track training attendance, coaching touchpoints, and time-to-first-deal. Clarity is power.",
    icon: BarChart3,
  },
];

export const TECH_STACK_COMPARISON = [
  {
    tool: "CRM System",
    cost: "$69-150/mo",
    problem: "Built for sales teams, not coaching workflows",
  },
  {
    tool: "Spreadsheets",
    cost: "Free (+ 8hrs/week)",
    problem: "Manual tracking, easy to lose agents in the cracks",
  },
  {
    tool: "Calendar Tools",
    cost: "$10-20/mo",
    problem: "No coaching context, just appointment slots",
  },
  {
    tool: "Task Manager",
    cost: "$10-15/mo",
    problem: "Your tasks, not agent accountability",
  },
  {
    tool: "Communication Log",
    cost: "Sticky notes",
    problem: "What did we talk about last week? Good luck.",
  },
];

export const HOW_IT_WORKS_STEPS = [
  {
    step: "1",
    title: "Add Your Agents",
    description:
      "Import your agent list in seconds. HelloPC becomes your single source of truth.",
    icon: Users,
  },
  {
    step: "2",
    title: "Track Everything",
    description:
      "Log calls, classes, transactions, and check-ins. HelloPC spots patterns and alerts you when agents go quiet.",
    icon: BarChart3,
  },
  {
    step: "3",
    title: "Stay Ahead of Problems",
    description:
      "See at-a-glance who needs attention, who's crushing it, and who's at risk. No more guessing.",
    icon: Target,
  },
  {
    step: "4",
    title: "Scale Your Impact",
    description:
      "Handle 2-3X more agents with the same effort. Spend less time on admin, more time actually coaching.",
    icon: Rocket,
  },
];

export const PRICING_TIERS = [
  {
    name: "Starter",
    price: "Free",
    period: "during Beta",
    description:
      "Everything you need to run a clean, organized productivity program — without spreadsheets.",
    tagline: "Free for Beta Users",
    features: [
      "Productivity dashboards",
      "Education calendar (no integrations)",
      "Instructor links",
      "Agent CRM (internal use)",
      "Transaction tracker",
      "Communication tracker",
      "PC task workflow tools",
    ],
    highlighted: true,
    available: true,
    badge: "Beta Access",
  },
  {
    name: "Team Edition",
    price: "Free",
    period: "during Beta",
    description:
      "Turn your productivity program into a two-way system where agents take ownership of their success.",
    tagline: "For offices with 20+ agents",
    features: [
      "Everything in Starter, plus:",
      "Agent Login / Agent Portal",
      "Personalized agent dashboards",
      "Self-service class registration",
      "Training paths & action plans",
      "Milestone tracking & progress",
      "Homework/task uploads",
      "Automated reminders",
      "PC resource library access",
    ],
    highlighted: false,
    available: true,
    badge: "Beta Access",
  },
  {
    name: "Pro with AI",
    price: "Free",
    period: "during Beta",
    description:
      "AI gives you a second Productivity Coach — without hiring one.",
    tagline: "For large offices & coaching teams",
    features: [
      "Everything in Team Edition, plus:",
      "Automatic meeting summaries",
      "Agent engagement analysis",
      "Red/yellow/green at-risk alerts",
      "Communication summaries",
      "AI 'next best action' recommendations",
      "Agent progress forecasting",
      "Instant class recaps",
      "Automated accountability reports",
    ],
    highlighted: false,
    available: true,
    badge: "Beta Access",
  },
];
